import React from 'react';
import { Badge, Button, Card } from 'react-bootstrap';
import { Check, RefreshCw } from 'lucide-react';
import type { CMWorkflowItem, CMWorkflowStatus, StatusHistoryEntry } from '../../../../../models/CatalogManagement';
import { isWorkflowStepCompleted, WORKFLOW_STEP_LABELS } from '../../../../../models/CatalogManagement';
import type { SkinSeoulAdminProduct } from '../../../../../models/ProductTypes';
import { AdminProductCardList } from './AdminProductCardList';

interface ChecklistItemProps {
  readonly status: CMWorkflowStatus;
  readonly label: string;
  readonly cmWorkflowData: CMWorkflowItem[] | undefined;
  readonly history?: StatusHistoryEntry | undefined;
}

const ChecklistItem: React.FC<ChecklistItemProps> = ({ status, label, cmWorkflowData, history }) => {
  const isCompleted = isWorkflowStepCompleted(cmWorkflowData, status);

  return (
    <div>
      <div className="d-flex align-items-center gap-2">
        <Check size={18} className={isCompleted ? 'text-success' : 'text-muted'} />
        <span className={isCompleted ? 'text-success' : 'text-muted'} style={{ fontSize: '0.95rem' }}>
          {label}
        </span>
      </div>
      {history && (
        <div className="ms-4 mt-1">
          <small className="text-muted">
            by {history.operator} at {new Date(history.updatedAt).toLocaleString()}
          </small>
        </div>
      )}
    </div>
  );
};

interface CMRequestSectionProps {
  readonly cmWorkflowData: CMWorkflowItem[] | undefined;
  readonly getStepHistory: (status: CMWorkflowStatus) => StatusHistoryEntry | undefined;
  readonly productDetails: SkinSeoulAdminProduct[];
  readonly productDetailsLoading: boolean;
  readonly step5Status: { disabled: boolean; reason?: string };
  readonly handleShowWaveModal: (status: CMWorkflowStatus, label: string) => void;
  readonly fetchProductDetails: (forceRefresh?: boolean) => Promise<void>;
}

export const CMRequestSection: React.FC<CMRequestSectionProps> = ({
  cmWorkflowData,
  getStepHistory,
  productDetails,
  productDetailsLoading,
  step5Status,
  handleShowWaveModal,
  fetchProductDetails,
}) => {
  return (
    <Card className="mb-4">
      <Card.Header>
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2">
            <h6 className="mb-0">Catalog Management Request</h6>
            {isWorkflowStepCompleted(cmWorkflowData, 'CM_REQUESTED' as CMWorkflowStatus) && (
              <Badge bg="success" className="d-flex align-items-center gap-1" style={{ fontSize: '0.7rem' }}>
                <Check size={12} />
                Completed
              </Badge>
            )}
          </div>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => fetchProductDetails(true)}
            disabled={productDetailsLoading}
            className="d-flex align-items-center gap-1"
          >
            <RefreshCw size={14} className={productDetailsLoading ? 'spin' : ''} />
            Refresh
          </Button>
        </div>
      </Card.Header>
      <Card.Body>
        <div className="row">
          {/* Left Column - Checklist */}
          <div className="col-md-7">
            <div className="mb-4 p-4 bg-light rounded">
              <h6 className="mb-3 text-primary">Review Checklist</h6>
              <div className="d-flex flex-column gap-3">
                <ChecklistItem
                  status={'PRODUCT_INFO_GENERATED' as CMWorkflowStatus}
                  label="Product Details Verified"
                  cmWorkflowData={cmWorkflowData}
                />
                <ChecklistItem
                  status={'CP_OPT_RESTRUCTURED' as CMWorkflowStatus}
                  label="Product Structure Completed & Restructured Products Available"
                  cmWorkflowData={cmWorkflowData}
                  history={getStepHistory('CP_OPT_RESTRUCTURED' as CMWorkflowStatus)}
                />
                <ChecklistItem
                  status={'WP_UPLOADED' as CMWorkflowStatus}
                  label="Uploaded to WordPress & SKU Mappings Available"
                  cmWorkflowData={cmWorkflowData}
                  history={getStepHistory('WP_UPLOADED' as CMWorkflowStatus)}
                />
                <ChecklistItem
                  status={'IMG_PROCESSED' as CMWorkflowStatus}
                  label="Images Processed & Thumbnail Registered"
                  cmWorkflowData={cmWorkflowData}
                  history={getStepHistory('IMG_PROCESSED' as CMWorkflowStatus)}
                />
              </div>
            </div>

            {/* Action Button */}
            <div className="mt-3">
              <div className="text-center">
                <p className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>
                  Submit this product for catalog change review. The Sourcing Manager or CM team will verify all details
                  before final approval.
                </p>
                <Button
                  variant={
                    isWorkflowStepCompleted(cmWorkflowData, 'CM_REQUESTED' as CMWorkflowStatus)
                      ? 'secondary'
                      : 'success'
                  }
                  disabled={
                    step5Status.disabled || isWorkflowStepCompleted(cmWorkflowData, 'CM_REQUESTED' as CMWorkflowStatus)
                  }
                  title={step5Status.disabled ? step5Status.reason : undefined}
                  onClick={() =>
                    handleShowWaveModal(
                      'CM_REQUESTED' as CMWorkflowStatus,
                      WORKFLOW_STEP_LABELS[4]?.labelAfter || 'Step 5',
                    )
                  }
                >
                  <Check size={18} className="me-2" />
                  {isWorkflowStepCompleted(cmWorkflowData, 'CM_REQUESTED' as CMWorkflowStatus)
                    ? 'Requested'
                    : 'Request Catalog Change'}
                </Button>
              </div>
            </div>
          </div>

          {/* Right Column - Product Details */}
          <div className="col-md-5">
            <div style={{ maxHeight: '580px', overflowY: 'auto', overflowX: 'hidden' }}>
              <AdminProductCardList products={productDetails} loading={productDetailsLoading} />
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};
